import type { ApiClient, HttpMethod, QueryValue } from './client';

/**
 * The pattern for adding a route: one line per route.
 *
 *   export const residentWatch = route<WatchResponse>()('GET', '/api/aithora/residents/:id/watch', { honest: true });
 *   const w = await residentWatch(client, { params: { id } });
 *
 * Path parameters are read from the path string itself, so `params` is typed and
 * required exactly when the path has them. `honest: true` makes the client refuse
 * an answer that does not carry the `real` flag.
 */

/** {id: string} for '/a/:id/b', {} for a path with no parameters. */
export type PathParams<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? { [K in Name | keyof PathParams<`/${Rest}`>]: string }
  : P extends `${string}:${infer Name}`
    ? { [K in Name]: string }
    : Record<never, never>;

type HasParams<P extends string> = keyof PathParams<P> extends never ? false : true;

export type CallArgs<P extends string, Q, B> = (HasParams<P> extends true
  ? { params: PathParams<P> }
  : { params?: undefined }) &
  ([Q] extends [void] ? { query?: undefined } : { query?: Q }) &
  ([B] extends [void] ? { body?: undefined } : { body: B }) & { signal?: AbortSignal; idempotencyKey?: string; timeoutMs?: number };

export interface RouteOptions {
  honest?: boolean;
  anonymous?: boolean;
  acceptStatuses?: readonly number[];
  /** Still sent while viewing as another role (it changes nothing others see). */
  readOnlySafe?: boolean;
}

export interface RouteFn<P extends string, R, Q, B> {
  (client: ApiClient, ...args: HasParams<P> extends true ? [CallArgs<P, Q, B>] : [B] extends [void] ? [CallArgs<P, Q, B>?] : [CallArgs<P, Q, B>]): Promise<R>;
  readonly method: HttpMethod;
  readonly path: P;
  readonly options: RouteOptions;
}

/** Fill :params into a path, encoding each value. Throws if one is missing. */
export function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined || v === null || v === '') throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(v);
  });
}

/**
 * Curried so the response type can be given explicitly while the path is inferred:
 * route<Response, Query, Body>()(method, path, options).
 */
export function route<R, Q extends Record<string, QueryValue> | void = void, B = void>() {
  return <P extends string>(method: HttpMethod, path: P, options: RouteOptions = {}): RouteFn<P, R, Q, B> => {
    const fn = async (client: ApiClient, args?: CallArgs<P, Q, B>): Promise<R> => {
      const a = (args ?? {}) as { params?: Record<string, string>; query?: Record<string, QueryValue>; body?: unknown; signal?: AbortSignal; idempotencyKey?: string; timeoutMs?: number };
      const res = await client.request<R>(method, fillPath(path, a.params), {
        query: a.query,
        body: a.body,
        signal: a.signal,
        idempotencyKey: a.idempotencyKey,
        timeoutMs: a.timeoutMs,
        honest: options.honest,
        anonymous: options.anonymous,
        acceptStatuses: options.acceptStatuses,
        readOnlySafe: options.readOnlySafe,
      });
      return res.data;
    };
    return Object.assign(fn, { method, path, options }) as unknown as RouteFn<P, R, Q, B>;
  };
}
